import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Newspaper, ArrowLeft, Facebook, Share2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import { SuggestLink } from "@/components/SuggestLink";

export default function AssociationNews() {
  const { slug, newsSlug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/public/associations/${slug}/actualites/${newsSlug}`)
      .then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug, newsSlug]);

  useSeo({
    title: data ? `${data.news.title} — ${data.association.name} | PROXIreduc-28` : "Actualité association",
    description: data?.news?.excerpt || "Actualité d'une association locale d'Eure-et-Loir.",
    canonicalPath: `/associations/${slug}/actualites/${newsSlug}`,
  });

  if (notFound) return (
    <div className="max-w-xl mx-auto py-20 px-4 text-center" data-testid="association-news-notfound">
      <Newspaper size={40} className="mx-auto text-slate-300" />
      <h1 className="mt-4 font-heading text-2xl font-bold">Actualité introuvable</h1>
      <Link to="/associations" className="mt-4 inline-block text-emerald-700 font-semibold hover:underline">← Retour à l'annuaire</Link>
    </div>
  );
  if (!data) return <div className="p-10 text-center text-slate-500">Chargement…</div>;

  const { association: a, news: n } = data;
  const pageUrl = `${window.location.origin}/associations/${a.slug}/actualites/${n.slug}`;
  const shareFb = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`, "_blank", "noopener,width=680,height=640");
  const copyLink = async () => { try { await navigator.clipboard.writeText(pageUrl); toast.success("Lien copié !"); } catch { toast.error("Impossible de copier."); } };

  const renderContent = (txt) => (txt || "").split("\n").map((line, i) => {
    if (line.startsWith("## ")) return <h2 key={i} className="font-heading font-bold text-xl mt-6 mb-2">{line.slice(3)}</h2>;
    if (!line.trim()) return <br key={i} />;
    return <p key={i} className="text-slate-700 leading-relaxed mb-3">{line}</p>;
  });

  return (
    <article className="max-w-3xl mx-auto px-4 py-10" data-testid="association-news-page">
      <Link to={`/associations/${a.slug}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-emerald-700">
        <ArrowLeft size={14} /> {a.name}
      </Link>
      {n.cover_image && <img src={n.cover_image} alt={n.title} className="mt-4 w-full rounded-2xl object-cover aspect-[16/9] border border-slate-100" />}
      {n.ai_generated && (
        <span className="mt-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-violet-100 text-violet-700"><Sparkles size={12} /> Rédigé avec l'IA</span>
      )}
      <h1 className="mt-3 font-heading text-3xl md:text-4xl font-extrabold text-slate-900" data-testid="association-news-title">{n.title}</h1>
      {n.excerpt && <p className="mt-2 text-lg text-slate-600">{n.excerpt}</p>}
      <div className="mt-4 flex gap-2">
        <Button variant="outline" size="sm" onClick={shareFb} data-testid="association-news-share-fb"><Facebook className="w-4 h-4 mr-1" /> Partager</Button>
        <Button variant="outline" size="sm" onClick={copyLink} data-testid="association-news-copy"><Share2 className="w-4 h-4" /></Button>
      </div>
      <div className="mt-6 prose-slate">{renderContent(n.content)}</div>
      <div className="mt-10 pt-6 border-t border-slate-100">
        <Link to={`/associations/${a.slug}`} className="text-emerald-700 font-semibold hover:underline">← Voir la page de {a.name}</Link>
      </div>
      <SuggestLink />
    </article>
  );
}
